// React & React Ecosystem
import React, { useContext, useEffect, useState } from 'react';

// Material UI
import { Tooltip } from '@mui/material';

// Context
import { ActiveChatContext } from '../../context/ActiveChatContext';
import { GlobalLoadingContext } from '../../context/GlobalLoadingContext';

// Components
import AddNewTag from '../../components/AddNewTag';
import CustomerComments from '../../components/CustomerComments';
import CustomerHistory from '../../components/CustomerHistory';
import CustomerOrders from '../../components/CustomerOrders';
import CustomerProducts from '../../components/CustomerProducts';
import CustomerReviewTicket from '../../components/CustomerReviewTicket';
import CustomerTickets from '../../components/CustomerTickets';
import FeedbacksList from '../../components/FeedbacksList';

// Modules
import { getData, putData } from '../../modules/fetch';

// Styles
import {
  AddNewIcon,
  Badgik,
  CommentIcon,
  CommentWithBadgikIcon,
  Container,
  FeedbackIcon,
  NavContainer,
  OrderIcon,
  OrderWithBadgikIcon,
  ProductIcon,
  ReviewIcon,
  TicketIcon,
  WorkHistoryIcon,
} from './styles';

const PanelCustomer = ({
  getInbox,
  activePanel,
  activeContent,
  togglePanels,
  users,
  isPreview,
  setIsPreview,
  setIsPreviewTicket,
}) => {
  const [tags, setTags] = useState([]);
  const [updateTags, setUpdateTags] = useState(false);
  const [activeChat, , activeChatDiff, , lastActiveChatId] = useContext(ActiveChatContext);
  const [, setLoading] = useContext(GlobalLoadingContext);
  const [orderCount, setOrderCount] = useState(0);
  useEffect(() => {
    if (activeChat?._id?.$oid !== lastActiveChatId) {
      setTags([]);
      if (
        activeChat?._id?.$oid &&
        activeChatDiff &&
        ('tags' in activeChatDiff.updated || 'tags' in activeChatDiff.added)
      ) {
        setLoading(false);
        getData(`/tags/read/${activeChat?._id?.$oid}`)
          .then((data) => {
            if (data && data.data.length > 0) {
              setTags(data.data);
            }
          })
          .catch((err) => {
            console.error(err);
          })
          .finally(() => {
            setLoading(true);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat]);
  useEffect(() => {
    if (updateTags) {
      setUpdateTags(false);
      putData(`/tags/update/${activeChat?._id?.$oid}`, {
        tags: tags,
      }).catch((err) => {
        console.error(err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTags]);

  useEffect(() => {
    let isMounted = true;
    if (activeChat?._id?.$oid && activeChat?._id?.$oid !== lastActiveChatId) {
      getData(`/customers/orders/${activeChat?._id?.$oid}/count`)
        .then((data) => {
          if (isMounted && data?.data) {
            setOrderCount(data.data.total);
          }
        })
        .catch((err) => {
          if (isMounted) console.error(err);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [activeChat, lastActiveChatId]);

  return (
    <Container>
      {activePanel && activeContent === 'tags' && (
        <AddNewTag
          getInbox={getInbox}
          tags={tags}
          setTags={setTags}
          setUpdateTags={setUpdateTags}
          users={users}
        />
      )}
      {activePanel && activeContent === 'orders' && <CustomerOrders />}
      {activePanel && activeContent === 'products' && <CustomerProducts />}
      {activePanel && activeContent === 'tickets' && (
        <CustomerTickets
          isPreview={isPreview}
          setIsPreview={setIsPreview}
          setIsPreviewTicket={setIsPreviewTicket}
        />
      )}
      {activePanel && activeContent === 'comments' && <CustomerComments />}
      {activePanel && activeContent === 'history' && <CustomerHistory />}
      {activePanel && activeContent === 'feedback' && <FeedbacksList />}
      {activePanel && activeContent === 'reviewTicket' && (
        <CustomerReviewTicket
          isPreview={isPreview}
          setIsPreview={setIsPreview}
          setIsPreviewTicket={setIsPreviewTicket}
        />
      )}
      <NavContainer>
        <Tooltip title="Tags">
          <AddNewIcon
            color={activeContent === 'tags' ? 'primary' : 'inherit'}
            onClick={() => {
              togglePanels('tags');
            }}
          />
        </Tooltip>
        {orderCount > 0 ? (
          <Badgik badgeContent={orderCount} color="primary">
            <Tooltip title="Orders">
              <OrderWithBadgikIcon
                color={activeContent === 'orders' ? 'primary' : 'inherit'}
                onClick={() => {
                  togglePanels('orders');
                }}
              />
            </Tooltip>
          </Badgik>
        ) : (
          <Tooltip title="Orders">
            <OrderIcon
              color={activeContent === 'orders' ? 'primary' : 'inherit'}
              onClick={() => {
                togglePanels('orders');
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="Products">
          <ProductIcon
            color={activeContent === 'products' ? 'primary' : 'inherit'}
            onClick={() => {
              togglePanels('products');
            }}
          />
        </Tooltip>
        <Tooltip title="Tickets">
          <TicketIcon
            color={activeContent === 'tickets' ? 'primary' : 'inherit'}
            onClick={() => {
              togglePanels('tickets');
            }}
          />
        </Tooltip>
        {activeChat?.comments?.length > 0 ? (
          <Badgik badgeContent={activeChat?.comments?.length} color="primary">
            <Tooltip title="Comments">
              <CommentWithBadgikIcon
                color={activeContent === 'comments' ? 'primary' : 'inherit'}
                onClick={() => {
                  togglePanels('comments');
                }}
              />
            </Tooltip>
          </Badgik>
        ) : (
          <Tooltip title="Comments">
            <CommentIcon
              color={activeContent === 'comments' ? 'primary' : 'inherit'}
              onClick={() => {
                togglePanels('comments');
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="History">
          <WorkHistoryIcon
            color={activeContent === 'history' ? 'primary' : 'inherit'}
            onClick={() => {
              togglePanels('history');
            }}
          />
        </Tooltip>
        <Tooltip title="Feedback">
          <FeedbackIcon
            color={activeContent === 'feedback' ? 'primary' : 'inherit'}
            onClick={() => {
              togglePanels('feedback');
            }}
          />
        </Tooltip>
        <Tooltip title="Review Ticket">
          <ReviewIcon
            color={activeContent === 'reviewTicket' ? 'primary' : 'inherit'}
            onClick={() => {
              togglePanels('reviewTicket');
            }}
          />
        </Tooltip>
      </NavContainer>
    </Container>
  );
};
export default React.memo(PanelCustomer);
